import React, { useEffect, useMemo, useState } from 'react';
import Icon from 'components/Utils/NewIcon';
import AddressFields from 'components/Patient/EditAddress/AddressFields';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import './style.scss';
import AppointmentUtils from 'services/AppointmentUtils';
import PatientBadge from 'components/Patient/PatientBadge/index';
import PatientSearchField from 'components/Patient/PatientSearchField';
import BookTimeOff from 'components/Physician/BookTimeOff';
import { AddCardModal } from 'components/Payments/CreditCardScreen/view';
import ClinicSelector from 'components/Utils/ClinicSelector';
import BookingConcernPicker from 'components/HealthRecords/Appointments/BookingConcernPicker';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import CalendarView from 'components/HealthRecords/Appointments/NewAppointment/CalendarView';
import SchedulePhysician from 'components/HealthRecords/Appointments/NewAppointment/SchedulePhysician';
import ResponsiveColumns from 'components/ResponsiveColumns';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import StaffTimePicker from 'components/Utils/StaffTimePicker';
import TextareaAutosize from 'components/Common/TextAreaAutosize';

const APPOINTMENT_TYPES = AppointmentUtils.getAppointmentTypes();

const NewAppointmentView = (props: any) => {
  let { isCalendarVisible } = props;

  let className = 'details-panel new-appointment padded-box scroll-list';
  if (isCalendarVisible) className += ' show-calendar';

  return (
    <div className={className}>
      <ResponsiveColumns
        columns={[
          {
            name: 'Appointment',
            element: (
              <div className="appointment-view">
                {!!props.confirmationDialog && (
                  <ConfirmationDialog
                    title="Confirmation Needed"
                    message={props.confirmationDialog}
                    open={!!props.confirmationDialog}
                    onConfirm={props.onConfirm}
                    onCancel={() => props.showConfirmationDialog(undefined)}
                    loadingState={props.isSubmitting}
                    additionalButton={
                      <DefaultButton
                        name="Book With No Payment"
                        id="alert-dialog-skip"
                        loading={props.isSubmitting}
                        onClick={props.onConfirmSkipPayment}
                        style={{ marginRight: '20px', width: '190px' }}
                        hide={props.confirmType !== 'payment'}
                      />
                    }
                  />
                )}
                <TopBar {...props} />
                {!props.patient && !!props.bookTimeOff && (
                  <BookTimeOff
                    physician={props.physician}
                    onClose={props.onClose}
                    startTime={props.startTime}
                  />
                )}
                {props.fixedPatient && (
                  <>
                    <span className="wide-label">Booking appointment for:</span>
                    <PatientBadge patient={props.patient} />
                  </>
                )}
                {!props.fixedPatient && !props.bookTimeOff && (
                  <>
                    <div
                      style={{
                        width: '100%',
                        margin: '15px 15px 15px 5px',
                        display: 'inline-block',
                      }}
                    >
                      <PatientSearchField
                        onSelect={props.onPatientSelected}
                        patient={props.patient}
                      />
                    </div>
                    {!!props.setBookTimeOff && !props.patient && (
                      <div
                        style={{
                          width: '100%',
                          margin: '15px 15px 15px 5px',
                          display: 'inline-block',
                        }}
                      >
                        <span className="small-title">Practitioner</span>
                        <DefaultButton
                          name="Book Time Off"
                          style={{ marginTop: '15px' }}
                          onClick={() => props.setBookTimeOff(true)}
                        />
                      </div>
                    )}
                  </>
                )}
                {!!props.patient && <AppointmentContent {...props} />}
              </div>
            ),
          },
          props.clinic_id && {
            name: 'Calendar',
            element: props.clinic_id ? <CalendarView {...props} /> : null,
          },
        ]}
      />
    </div>
  );
};

const AppointmentContent = (props: any) => {
  let { type, clinic_id, control, setValue } = props;

  const patientId = props.patient?.id;

  const appointment = useMemo(() => {
    return {
      type: props.type,
      date: props.date,
      clinic_id: props.clinic_id,
      patient_id: patientId,
    };
  }, [props.type, props.date, props.clinic_id, patientId]);

  return (
    <div className="data-content">
      <DateField control={control} />
      <Concern {...props} />
      <span className="small-title mt-2">Notes</span>
      <TextareaAutosize name="notes" control={control} cacheMeasurements />
      {props.complaints && (
        <>
          <span className="small-title mt-2">Complaints</span>
          <TextareaAutosize name="complaints" control={control} disabled={true} />
        </>
      )}
      {type === APPOINTMENT_TYPES.home && <AddressField {...props} />}
      <React.Fragment key={clinic_id}>
        <ClinicSelector
          style={{ width: '100%', marginBottom: '10px' }}
          value={clinic_id}
          hideLabel={false}
          setValue={(id: string) => {
            setValue('clinic_id', id);
            props.selectPhysician();
          }}
        />
        <span className="small-title">Practitioner</span>
        <SchedulePhysician
          appointment={appointment}
          selectedId={props.selectedPhysician}
          onClick={props.selectPhysician}
          physicians={props.physicians}
        />
        <BookButtons {...props} />
      </React.Fragment>
    </div>
  );
};

const AddressField = ({ onAddressChange, setAddress, address }: any) => (
  <div className="row" style={{ marginTop: '10px' }}>
    <span className="small-title">Address</span>
    <AddressFields
      showNotes={true}
      style={{ width: '100%' }}
      address={address || {}}
      onChange={onAddressChange}
      onSearchResult={setAddress}
    />
  </div>
);

const Concern = (props: any) => {
  const { setValue, patient } = props;
  if (!patient?.id) return null;

  return (
    <BookingConcernPicker
      patientId={patient.id}
      value={props.concern_id}
      deliveryValue={props.type}
      deliveryTelemedicineMethod={props.telemedicine_method}
      withDeliveryOptions
      label="Appointment"
      onChange={(concernId, selection) => {
        setValue('concern_id', concernId);
        if (selection?.deliveryOption) {
          Object.keys(selection.deliveryOption).forEach((key: string) => {
            setValue(key, selection.deliveryOption[key]);
          });
        }
      }}
    />
  );
};

const DateField = ({ control }: { control: any }) => (
  <div className="row">
    <DefaultDatePicker
      name="date"
      control={control}
      label="Day"
      style={{
        width: 'calc(50% - 10px)',
        display: 'inline-block',
        marginRight: '20px',
      }}
    />
    <StaffTimePicker
      name="time"
      control={control}
      label="Time"
      style={{
        width: 'calc(50% - 10px)',
        display: 'inline-block',
      }}
    />
  </div>
);

const CreditCardButton = ({ patient }: { patient: any }) => {
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);

  const creditCards = useAppSelector((state) => state.payments.creditCards);

  const patientId = patient.id;

  const patientCards = creditCards[patientId];

  useEffect(() => {
    if (!patientCards) {
      dispatch(api.Payments.getCreditCards({ patientId }));
    }
  }, [patientCards, patientId, dispatch]);

  const showCreditCardButton = useMemo(() => {
    if (!patientCards) return false;
    return patientCards.length === 0;
  }, [patientCards]);

  return (
    showCreditCardButton && (
      <div className="button-wrapper add-credit-card">
        <AddCardModal
          showModal={showModal}
          onAddSuccess={() => setShowModal(false)}
          patient={patient}
          openModal={setShowModal}
        />
        <DefaultButton
          name="Add Credit Card"
          icon="credit-card"
          style={{ width: '80%', margin: '10px auto', display: 'block' }}
          onClick={() => setShowModal(true)}
        />
      </div>
    )
  );
};

const BookButtons = ({ isButtonEnabled, isSubmitting, bookAppointment, patient }: any) => {
  return (
    <div className="buttons">
      <DefaultButton
        name="Book Appointment"
        className="blue"
        icon="calendar-clock"
        loading={isSubmitting}
        disabled={!isButtonEnabled}
        onClick={bookAppointment}
        style={{ display: 'block', width: '80%', margin: '15px auto' }}
      />
      <CreditCardButton patient={patient} />
    </div>
  );
};

const TopBar = ({ allowClose, onClose, setCalendarVisible, isCalendarVisible }: any) => {
  return (
    allowClose !== undefined && (
      <div className="top-bar">
        <div className="close">
          <Icon src="xmark" onClick={onClose} />
        </div>
        <span className="small-title">New Appointment</span>
        <Icon
          width="20px"
          src="calendar-clock"
          onClick={() => {
            setCalendarVisible(!isCalendarVisible);
          }}
          className="show-calendar-icon"
          active={isCalendarVisible}
        />
      </div>
    )
  );
};

export default NewAppointmentView;
